import deepmerge from 'deepmerge';
import { BirthDateValidation } from '../components/atoms/DateInput/getBirthDateValidator';
import { PhoneNumberValidation } from '../components/atoms/PhoneInput/getPhoneNumberValidator';
import { NotificationLevel } from '../components/molecules/Notifications';
import defaultConfig from '../defaults/config.json';
import defaultTranslations from '../defaults/translations.json';
import { Coordinates } from '../types/collectionPoints';
import { AppQualifier } from './appQualifier';
import { Country, Currency } from './countryData';
import { Validation } from './getValidator';
import { reportErrorToSentry } from './initializeSentry';
import { Step } from './routing';

export type BasicFeature = { enabled: boolean };

export type NotImplemented = Record<string, never>;

export type Provider = { key: string; color?: string };

export type AddressForm = {
  specialComponent?: boolean;
  title?: BasicFeature;
  streetHouseNumber: {
    separateFields: boolean;
    houseNumber: { position: 'first' | 'second' } & Validation;
    street?: Validation;
  } & Validation;
  additional: BasicFeature & Validation;
  zipCodeCity: {
    zipCode: BasicFeature & { position: 'first' | 'second' } & Validation & {
        allowList?: boolean;
        blockList?: boolean;
      } & { completion?: BasicFeature };
    city?: Validation & { completion?: BasicFeature };
  };
  state?: BasicFeature & Validation & { select: BasicFeature };
};

export type ConfigNotification = {
  textTranslationKey: string;
  startDateTime?: string;
  endDateTime?: string;
  steps?: Step[];
  type?: NotificationLevel;
};

type Link = { key: string; link: string };

export type ConditionField =
  | 'carrierGroupKey'
  | 'shippingOptionKey'
  | 'collectionPointType'
  | 'paymentOptionKey'
  | 'shippingCountry'
  | 'billingCountry'
  | 'minBasketTotal';
export type Condition = Partial<Record<ConditionField, string[]> & { minBasketTotal?: number }>;
export type Conditions = { enabled?: Condition; required?: Condition };
export type TextsConfig = {
  name: string;
  i18nKeys: { label: string; headline?: string };
  conditions?: Conditions;
};
export type CheckboxConfig = TextsConfig & { required?: boolean };
export type DynamicFields = {
  taxId?: Validation & { conditions?: Conditions };
  birthDate?: BirthDateValidation & { conditions?: Conditions };
  phoneNumber?: PhoneNumberValidation & { conditions?: Conditions };
  logisticsPhoneNumber?: PhoneNumberValidation & { conditions?: Conditions };
  carrierCustomerId?: Validation & { conditions?: Conditions };
  checkboxes?: CheckboxConfig[];
  texts?: TextsConfig[];
};
export type DynamicFieldAreaKey =
  | 'register'
  | 'guest'
  | 'shippingAddress'
  | 'collectionPoint'
  | 'billingAddress'
  | 'paymentOptions'
  | 'payment';

export type Session = {
  jwtSecret: string;
  token?: string;
  correlationId?: string;
};

export type Config = AppQualifier & {
  header: {
    desktopHeight: number;
    mobileHeight: number;
  };
  statePollInterval: number;
  paymentConfirmationPollTimeout: number;
  features: {
    breadcrumbs: BasicFeature;
    expressCheckout: {
      payPal: BasicFeature & NotImplemented;
      applePay: BasicFeature & NotImplemented;
      amazonPay: BasicFeature & NotImplemented;
    };
    passwordVisibilityToggle: BasicFeature;
    thirdPartySignIn: BasicFeature & {
      disableFirstPartySignIn: boolean;
      providers: Provider[];
    };
    guestCheckout: BasicFeature;
    addressBook: BasicFeature;
    vouchers: BasicFeature & Validation;
    giftCards: BasicFeature & Validation & { pin: BasicFeature & Validation };
    loyaltyProgram: BasicFeature & Validation & { type: string; icon: BasicFeature };
    lowestPriorPrice: BasicFeature;
    sentry: BasicFeature;
    appDeepLinkRewrites: BasicFeature;
  };
  currency: Currency;
  formats: {
    date: string;
    currency: 'symbol' | 'code' | 'name' | 'narrowSymbol';
  };
  carrierOptions: { position: 'shippingTop' | 'shippingBottom' | 'payment' };
  collectionPoints: { defaultMaximumDistance: number; maximumDistanceOptions: number[] };
  addressForm: Record<Country, AddressForm>;
  dynamicFields: Partial<Record<DynamicFieldAreaKey, DynamicFields>>;
  links: Link[];
  footer: {
    uspIcons: string[];
    paymentIcons: string[];
    badges: Link[];
  };
  notifications: ConfigNotification[];
  customizedAssets: string[];
  externalAssets: Record<string, string>;
  styles: Record<string, string>;
  isProduction: boolean;
  availableCountries: {
    shipping: { iso2Code: Country; centerCoordinates: Coordinates }[];
    billing?: { iso2Code: Country }[];
  };
  session: Session;
  translations: Record<string, any>;
  urls: { passwordResetConfirmation: string };
};

// export type BackendConfig = {
//   availableCountries: {
//     shipping: Country[];
//     billing?: Country[];
//   };
//   environment: 'staging' | 'production' | 'development';
//   features: {
//     accountArea: BasicFeature & { shippingSectionEnabled: boolean };
//     carrierPhone: BasicFeature & { restrictByRegion: boolean };
//     checkoutNotifications: {
//       addressUpdateNotification: Feature;
//       deliveryDelayNotification: Feature;
//       returnHandlingDelayNotification: Feature;
//     };
//     checkoutPageRouting: { routingBehavior: string }; // 'lastPossibleStep' ??
//     checkoutPageStyling: {
//       customerNameInHeader: BasicFeature & {
//         default: string;
//         prefix: string;
//       };
//     };
//     collectionPoints: { supportForwardingAddress: boolean };
//     orderDetails: { useRatepayReferenceKey: boolean };
//     payback: BasicFeature;
//     referencePrices: BasicFeature;
//     registration: { enableGuestCustomers: boolean };
//     returnDocuments: BasicFeature & { carriers: string[]; defaultCarrier: string };
//     returnLabels: BasicFeature;
//     returnPickup: BasicFeature;
//     vwoIntegration: BasicFeature;
//   };
//   language: string;
//   locale: string;
//   payment: {
//     cashOnDeliveryMethods: string[];
//   };
//   session: {
//     jwtSecret: string;
//   };
//   shopUrls: {
//     back: { fullCustomer: string; guest: string };
//     contact: string;
//     dataProtection: string;
//     help: string;
//     imprint: string;
//     logout: string;
//     paymentInformation: string;
//     productImage: string;
//     shopDomain: string;
//     termsOfService: string;
//     termsOfUse: string;
//   };
//   version: string;
// };

const fullPath = (document.currentScript as any)?.src ?? '';
export const DISTRIBUTION_URL = fullPath.substring(0, fullPath.lastIndexOf('/'));
const CDN_HOST = new URL(fullPath).origin;

export let config: Config;

export const getCdnShopUrl = ({ environment, shop }: Pick<AppQualifier, 'environment' | 'shop'>) =>
  environment ? `${CDN_HOST}/shops/${environment}/${shop}` : `${CDN_HOST}/shops/${shop}`;

export const getAssetUrl = (path: string) => {
  if (path in config.externalAssets) {
    return config.externalAssets[path];
  } else if (config.customizedAssets.includes(path)) {
    return `${getCdnShopUrl(config)}/assets/${path}`;
  } else {
    return `${DISTRIBUTION_URL}/assets/${path}`;
  }
};

const validateAddressFormConfig = (_config: Config) => {
  const allCountries = [
    ..._config.availableCountries.shipping.map(({ iso2Code }) => iso2Code),
    ...(_config.availableCountries.billing?.map(({ iso2Code }) => iso2Code) ?? [_config.country]),
  ];
  const uniqueCountries = [...new Set(allCountries)];
  uniqueCountries.forEach(country => {
    if (!(country in _config.addressForm)) {
      console.error(`No address form config found for country ${country}`);
      reportErrorToSentry(new Error(`No address form config found for country ${country}`));
    }
  });
};

const overwriteMerge = (_: unknown, sourceArray: unknown[]) => sourceArray;

const loadCdnConfig = async (appQualifier: AppQualifier) => {
  const cdnConfigUrl = `${getCdnShopUrl(appQualifier)}/config.json`;
  const response = await fetch(cdnConfigUrl);
  if (response.status >= 400) {
    console.warn(`Could not load shop config (${cdnConfigUrl}). Using default config only.`);
    return {};
  } else {
    return response.json();
  }
};

const loadBackendConfig = async (
  { apiHost, appId }: AppQualifier,
  session?: Pick<Session, 'token' | 'correlationId'>
) => {
  const headers: Record<string, string> = {
    'X-Shop-ID': appId.toString(),
  };
  if (session?.token) {
    headers['X-Session'] = session.token;
  }
  if (session?.correlationId) {
    headers['X-Correlation-ID'] = session.correlationId;
  }
  return (await fetch(`${apiHost}/api/co/v3/state/config`, { credentials: 'include', headers })).json();
};

export const loadConfig = async (appQualifier: AppQualifier, session?: Pick<Session, 'token' | 'correlationId'>) => {
  const [backendConfig, { localeOverwrites, countryOverwrites, ...cdnConfig }] = await Promise.all([
    loadBackendConfig(appQualifier, session),
    loadCdnConfig(appQualifier),
  ]);

  config = deepmerge.all(
    [
      { translations: defaultTranslations }, // TODO: remove default translations once state/config endpoint provides them: https://aboutyou.atlassian.net/browse/CT-265870
      defaultConfig,
      backendConfig,
      cdnConfig,
      countryOverwrites?.[appQualifier.country] ?? {},
      localeOverwrites?.[appQualifier.locale] ?? {},
      appQualifier,
      { session: session ?? {}, isProduction: backendConfig.environment === 'production' },
    ],
    { arrayMerge: overwriteMerge }
  ) as Config;

  validateAddressFormConfig(config);

  return config;
};
